/* DhfShareBell — Turbo-only dashboard notification bell.
   Shows the owner (creator) doctor when a patient shares their Digital Health
   File with another doctor. Rendered ONLY for tier === 'turbo'. */
import { useEffect, useRef, useState } from 'react';
import { Bell, FileCheck2, X, Check } from 'lucide-react';
import api from '@/lib/api';

function timeAgo(iso) {
  if (!iso) return '';
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

export default function DhfShareBell() {
  const [open, setOpen] = useState(false);
  const [shares, setShares] = useState([]);
  const [unread, setUnread] = useState(0);
  const ref = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get('/dashboard/dhf-shares');
      if (data.tier_ok === false) return;
      setShares(data.shares || []);
      setUnread(data.unread || 0);
    } catch { /* silent */ }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const markAll = async () => {
    try {
      await api.post('/dashboard/dhf-shares/read-all');
      setShares((prev) => prev.map((s) => ({ ...s, read: true })));
      setUnread(0);
    } catch { /* silent */ }
  };

  const markOne = async (id) => {
    try {
      await api.post(`/dashboard/dhf-shares/${id}/read`);
      setShares((prev) => prev.map((s) => (s.id === id ? { ...s, read: true } : s)));
      setUnread((u) => Math.max(0, u - 1));
    } catch { /* silent */ }
  };

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((o) => !o)}
        className="relative flex items-center justify-center w-9 h-9 rounded-full bg-white border border-slate-200 text-slate-600 hover:bg-sky-50 hover:text-sky-700 transition-colors"
        title="Health files shared with other doctors"
        data-testid="dhf-share-bell"
      >
        <Bell size={17} />
        {unread > 0 && (
          <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-rose-500 text-white text-[10px] font-bold flex items-center justify-center" data-testid="dhf-share-unread">
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-[340px] max-w-[90vw] bg-white rounded-2xl border border-slate-200 shadow-xl z-50 overflow-hidden" data-testid="dhf-share-panel">
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
            <div>
              <p className="text-sm font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>Shared health files</p>
              <p className="text-[11px] text-slate-400">When your patients share their file with another doctor</p>
            </div>
            <button onClick={() => setOpen(false)} className="text-slate-400 hover:text-slate-600"><X size={16} /></button>
          </div>

          {shares.length === 0 ? (
            <div className="px-4 py-8 text-center" data-testid="dhf-share-empty">
              <FileCheck2 size={26} className="text-slate-300 mx-auto mb-2" />
              <p className="text-[13px] text-slate-500">No shares yet.</p>
              <p className="text-[11px] text-slate-400 mt-1">You’ll be alerted the moment a patient shares one of your files.</p>
            </div>
          ) : (
            <>
              <div className="max-h-[360px] overflow-y-auto">
                {shares.map((s) => (
                  <button
                    key={s.id}
                    onClick={() => !s.read && markOne(s.id)}
                    className={`w-full text-left px-4 py-3 border-b border-slate-50 hover:bg-slate-50 transition-colors ${s.read ? '' : 'bg-sky-50/60'}`}
                    data-testid={`dhf-share-item-${s.id}`}
                  >
                    <div className="flex items-start gap-2.5">
                      <span className="mt-0.5 w-8 h-8 rounded-full bg-emerald-50 text-emerald-600 flex items-center justify-center shrink-0">
                        <FileCheck2 size={15} />
                      </span>
                      <div className="min-w-0 flex-1">
                        <p className="text-[13px] text-slate-800 leading-snug">
                          <b>{s.patient_name || 'A patient'}</b> shared their Digital Health File with <b>{s.shared_with_doctor_name || 'another doctor'}</b>.
                        </p>
                        <p className="text-[11px] text-slate-400 mt-1">
                          {s.appointment_date ? `Follow-up ${s.appointment_date}${s.appointment_time ? ' · ' + s.appointment_time : ''} · ` : ''}{timeAgo(s.created_at)}
                          {s.file_id ? ` · ${s.file_id}` : ''}
                        </p>
                      </div>
                      {!s.read && <span className="mt-1 w-2 h-2 rounded-full bg-sky-500 shrink-0" />}
                    </div>
                  </button>
                ))}
              </div>
              {unread > 0 && (
                <button onClick={markAll} className="w-full py-2.5 text-[12px] font-semibold text-sky-700 hover:bg-sky-50 flex items-center justify-center gap-1.5 border-t border-slate-100" data-testid="dhf-share-mark-all">
                  <Check size={14} /> Mark all as read
                </button>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
